// Mock resolver — routes every HTTP verb + path the frontend uses to an
// in-browser handler backed by `mockStore`. When the real backend is
// available, `client.ts` bypasses this module entirely.
//
// New endpoint? Add a matcher here AND expose a real route in
// docs/API_CONTRACT.md. Keep the two in lockstep.

import { getMockStore, overviewSummary, clusteredLogs, diffFor } from "./mockStore";
import {
  clusterSummaryFor, clusterTopologyFor, k8sObjectsFor,
  deploymentTimelineFor, containerDetailFor, globalSearch, buildInfrastructureTree,
} from "@coremonit/mock-data";
import type {
  Service, ServiceCreateInput,
  Project, ProjectCreateInput, ProjectGroup, ProjectGroupCreateInput,
  ProjectMember, ProjectMemberInput, ProjectsSummary, HierarchyPreset,
} from "@coremonit/types";
import { ApiError } from "./types";

const DEMO_USER = {
  id: "u_demo",
  email: "admin@coremonit.io",
  name:  "Ada Admin",
  role:  "admin" as const,
  createdAt: new Date().toISOString(),
};

type Query = Record<string, string | number | boolean | undefined>;

function resolveProject(store: ReturnType<typeof getMockStore>, idOrKey: string) {
  return store.projects.find((x) => x.id === idOrKey || x.key === idOrKey);
}

function matchPath(path: string, pattern: string): Record<string, string> | null {
  const p = path.split("?")[0].split("/").filter(Boolean);
  const q = pattern.split("/").filter(Boolean);
  if (p.length !== q.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < q.length; i++) {
    const seg = q[i];
    if (seg.startsWith(":")) params[seg.slice(1)] = decodeURIComponent(p[i]);
    else if (seg !== p[i]) return null;
  }
  return params;
}

function ok<T>(body: T): T { return body; }

function filterList(items: Service[], q: Query): Service[] {
  let out = items;
  const s = (v: unknown) => String(v ?? "").toLowerCase();
  if (q.q)       out = out.filter((x) => s(x.name).includes(s(q.q)) || s(x.team).includes(s(q.q)));
  if (q.status)  out = out.filter((x) => x.status      === q.status);
  if (q.env)     out = out.filter((x) => x.environment === q.env);
  if (q.team)    out = out.filter((x) => x.team        === q.team);
  if (q.tag)     out = out.filter((x) => x.tags.includes(String(q.tag)));
  if (q.project) out = out.filter((x) => x.projectId   === q.project);
  if (q.group)   out = out.filter((x) => x.groupId     === q.group);
  return out;
}

export async function mockRequest<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
  // Simulate a tiny bit of network latency so loading skeletons flash.
  await new Promise((r) => setTimeout(r, 60));
  const store = getMockStore();
  const M = method.toUpperCase();
  let m: Record<string, string> | null;

  // ── auth ─────────────────────────────────────────────────────────────
  if (M === "POST" && path === "/auth/login") {
    const b = (body ?? {}) as { email?: string; password?: string };
    if (!b.email || !b.password) throw new ApiError(400, { error: "invalid_body" });
    return ok({ token: "mock.jwt.token", user: DEMO_USER }) as unknown as T;
  }
  if (M === "POST" && path === "/auth/logout") return ok({ ok: true }) as unknown as T;
  if (M === "GET"  && path === "/auth/me")     return ok({ user: DEMO_USER }) as unknown as T;

  // ── services / overview ──────────────────────────────────────────────
  if (M === "GET" && path === "/services")         return ok({ items: filterList(store.services, query ?? {}), total: store.services.length }) as unknown as T;
  if (M === "GET" && path === "/services/summary") return ok(overviewSummary()) as unknown as T;
  if (M === "GET" && (m = matchPath(path, "/services/:id"))) {
    const svc = store.services.find((s) => s.id === m!.id);
    if (!svc) throw new ApiError(404, { error: "not_found" });
    return ok(svc) as unknown as T;
  }
  if (M === "POST" && path === "/services") {
    const d = body as ServiceCreateInput;
    const id  = `svc_user_${Date.now().toString(36)}`;
    const now = new Date().toISOString();
    const svc: Service = {
      id, name: d.name, slug: d.name, description: d.description,
      owner: d.owner, team: d.team, tags: d.tags ?? [],
      environment: d.environment, language: d.language, runtime: d.runtime, version: d.version,
      status: "unknown",
      deployment: {
        type: (d.deployment?.type ?? "kubernetes") as Service["deployment"]["type"],
        cluster:   d.deployment?.cluster,
        namespace: d.deployment?.namespace,
        region:    d.deployment?.region,
      },
      instrumentation: (d.instrumentation ?? ["opentelemetry"]) as Service["instrumentation"],
      createdAt: now, updatedAt: now,
      latencyP50Ms: 0, latencyP95Ms: 0, latencyP99Ms: 0,
      errorRatePct: 0, requestsPerMin: 0, availabilityPct: 100,
      replicas: { desired: 0, available: 0 },
      restarts24h: 0, oomKills24h: 0,
    };
    store.services.push(svc);
    return ok({ service: svc }) as unknown as T;
  }
  if (M === "POST" && (m = matchPath(path, "/services/:id/validate-connection"))) {
    return ok({
      checks: [
        { id: "connectivity", label: "Connectivity",   status: "passed", latencyMs:  42, message: "TCP handshake ok" },
        { id: "metrics",      label: "Metrics ingest", status: "passed", latencyMs:  60, message: "Received 3 sample series" },
        { id: "logs",         label: "Log stream",     status: "passed", latencyMs: 180, message: "Tailed 25 log lines" },
        { id: "traces",       label: "Trace ingest",   status: "passed", latencyMs: 220, message: "OTLP endpoint reachable" },
        { id: "health",       label: "Health probe",   status: "passed", latencyMs:  30, message: "GET /healthz returned 200" },
      ],
    }) as unknown as T;
  }
  if (M === "POST" && (m = matchPath(path, "/services/:id/collect-baseline"))) {
    const svc = store.services.find((s) => s.id === m!.id);
    if (!svc) throw new ApiError(404, { error: "not_found" });
    return ok({
      window: "5m",
      baseline: { cpuPct: 42.1, memPct: 58.4, latencyP95Ms: svc.latencyP95Ms, errorRatePct: svc.errorRatePct, requestsPerMin: svc.requestsPerMin, saturationPct: 61 },
    }) as unknown as T;
  }

  // ── projects ─────────────────────────────────────────────────────────
  if (M === "GET" && path === "/projects") {
    return ok({ items: store.projects, total: store.projects.length }) as unknown as T;
  }
  if (M === "GET" && path === "/projects/summary") {
    const totals = store.projects.reduce(
      (acc, p) => {
        acc.byStatus[p.status]++;
        acc.totals.incidents += p.incidentCount;
        return acc;
      },
      {
        totals:   { projects: store.projects.length, services: store.services.length, members: store.members.length, incidents: 0 },
        byStatus: { healthy: 0, warning: 0, critical: 0, unknown: 0 } as ProjectsSummary["byStatus"],
      },
    );
    return ok({
      ...totals,
      tiles: store.projects.map((p) => ({
        id: p.id, key: p.key, name: p.name, color: p.color, icon: p.icon, status: p.status,
        environments: p.environments, serviceCount: p.serviceCount, memberCount: p.memberCount,
        incidentCount: p.incidentCount, metrics: p.metrics,
      })),
    }) as unknown as T;
  }
  if (M === "GET" && (m = matchPath(path, "/projects/:id"))) {
    const p = store.projects.find((x) => x.id === m!.id || x.key === m!.id);
    if (!p) throw new ApiError(404, { error: "not_found" });
    return ok(p) as unknown as T;
  }
  if (M === "POST" && path === "/projects") {
    const d = (body ?? {}) as ProjectCreateInput;
    if (!d.key || !d.name || !d.ownerEmail) throw new ApiError(400, { error: "invalid_body" });
    if (store.projects.some((x) => x.key === d.key)) throw new ApiError(409, { error: "conflict", message: "key already exists" });
    const now = new Date().toISOString();
    const p: Project = {
      id: `proj_${d.key}`,
      key: d.key,
      name: d.name,
      description: d.description ?? "",
      color: d.color ?? "#2D7FF9",
      icon:  d.icon  ?? "Boxes",
      status: "unknown",
      environments: [],
      ownerEmail: d.ownerEmail,
      createdAt: now,
      updatedAt: now,
      serviceCount: 0, groupCount: 0, memberCount: 1, incidentCount: 0,
      metrics: { availabilityPct: 100, latencyP95Ms: 0, errorRatePct: 0, requestsPerMin: 0, deploymentSuccessPct: 100 },
    };
    store.projects.push(p);
    store.members.push({
      userId: `usr_${d.ownerEmail.split("@")[0].replace(/\./g, "-")}`,
      projectId: p.id, email: d.ownerEmail,
      name: d.ownerEmail.split("@")[0], role: "project_admin", addedAt: now,
    });
    return ok({ project: p }) as unknown as T;
  }
  if (M === "PATCH" && (m = matchPath(path, "/projects/:id"))) {
    const p = store.projects.find((x) => x.id === m!.id || x.key === m!.id);
    if (!p) throw new ApiError(404, { error: "not_found" });
    const patch = (body ?? {}) as Partial<Pick<Project, "name" | "description" | "color" | "icon" | "ownerEmail">>;
    Object.assign(p, patch, { updatedAt: new Date().toISOString() });
    return ok({ project: p }) as unknown as T;
  }
  if (M === "DELETE" && (m = matchPath(path, "/projects/:id"))) {
    const idx = store.projects.findIndex((x) => x.id === m!.id || x.key === m!.id);
    if (idx < 0) throw new ApiError(404, { error: "not_found" });
    const removed = store.projects.splice(idx, 1)[0];
    // Cascade — un-assign services and drop the project's groups + members.
    for (const s of store.services) if (s.projectId === removed.id) { delete s.projectId; delete s.groupId; }
    for (let i = store.groups.length  - 1; i >= 0; i--) if (store.groups[i].projectId  === removed.id) store.groups.splice(i, 1);
    for (let i = store.members.length - 1; i >= 0; i--) if (store.members[i].projectId === removed.id) store.members.splice(i, 1);
    return ok({ ok: true }) as unknown as T;
  }

  // ── projects → groups ────────────────────────────────────────────────
  if (M === "GET" && (m = matchPath(path, "/projects/:id/groups"))) {
    const p = store.projects.find((x) => x.id === m!.id || x.key === m!.id);
    if (!p) throw new ApiError(404, { error: "not_found" });
    return ok({ items: store.groups.filter((g) => g.projectId === p.id) }) as unknown as T;
  }
  if (M === "POST" && (m = matchPath(path, "/projects/:id/groups"))) {
    const p = store.projects.find((x) => x.id === m!.id || x.key === m!.id);
    if (!p) throw new ApiError(404, { error: "not_found" });
    const d = (body ?? {}) as ProjectGroupCreateInput;
    if (!d.name || !d.kind) throw new ApiError(400, { error: "invalid_body" });
    const g: ProjectGroup = {
      id: `grp_${p.key}_${d.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}_${Date.now().toString(36)}`,
      projectId: p.id,
      name: d.name,
      description: d.description,
      kind: d.kind,
      targets: d.targets ?? [],
      color: d.color ?? "#2D7FF9",
      serviceCount: 0,
      createdAt: new Date().toISOString(),
    };
    // Recompute serviceCount by testing membership against known services.
    g.serviceCount = store.services.filter((s) => {
      if (s.projectId !== p.id) return false;
      if (g.kind === "environment") return g.targets.includes(s.environment);
      if (g.kind === "provider")    return g.targets.includes(s.deployment.type);
      if (g.kind === "cluster")     return !!s.deployment.cluster && g.targets.includes(s.deployment.cluster);
      if (g.kind === "region")      return !!s.deployment.region  && g.targets.includes(s.deployment.region);
      if (g.kind === "custom")      return g.targets.includes(s.id);
      return false;
    }).length;
    store.groups.push(g);
    p.groupCount = store.groups.filter((x) => x.projectId === p.id).length;
    return ok({ group: g }) as unknown as T;
  }
  if (M === "PATCH" && (m = matchPath(path, "/projects/:projectId/groups/:groupId"))) {
    const p = store.projects.find((x) => x.id === m!.projectId || x.key === m!.projectId);
    if (!p) throw new ApiError(404, { error: "not_found" });
    const g = store.groups.find((x) => x.id === m!.groupId && x.projectId === p.id);
    if (!g) throw new ApiError(404, { error: "not_found" });
    Object.assign(g, body ?? {});
    return ok({ group: g }) as unknown as T;
  }
  if (M === "DELETE" && (m = matchPath(path, "/projects/:projectId/groups/:groupId"))) {
    const idx = store.groups.findIndex((x) => x.id === m!.groupId);
    if (idx < 0) throw new ApiError(404, { error: "not_found" });
    const removed = store.groups.splice(idx, 1)[0];
    for (const s of store.services) if (s.groupId === removed.id) delete s.groupId;
    const p = store.projects.find((x) => x.id === removed.projectId);
    if (p) p.groupCount = store.groups.filter((x) => x.projectId === p.id).length;
    return ok({ ok: true }) as unknown as T;
  }

  // ── projects → services (scoped) ─────────────────────────────────────
  if (M === "GET" && (m = matchPath(path, "/projects/:id/services"))) {
    const p = store.projects.find((x) => x.id === m!.id || x.key === m!.id);
    if (!p) throw new ApiError(404, { error: "not_found" });
    const groupId = (query?.group as string | undefined) || undefined;
    let items = store.services.filter((s) => s.projectId === p.id);
    if (groupId) items = items.filter((s) => s.groupId === groupId);
    return ok({ items, total: items.length }) as unknown as T;
  }

  // ── projects → members ───────────────────────────────────────────────
  if (M === "GET" && (m = matchPath(path, "/projects/:id/members"))) {
    const p = store.projects.find((x) => x.id === m!.id || x.key === m!.id);
    if (!p) throw new ApiError(404, { error: "not_found" });
    return ok({ items: store.members.filter((x) => x.projectId === p.id) }) as unknown as T;
  }
  if (M === "POST" && (m = matchPath(path, "/projects/:id/members"))) {
    const p = store.projects.find((x) => x.id === m!.id || x.key === m!.id);
    if (!p) throw new ApiError(404, { error: "not_found" });
    const d = (body ?? {}) as ProjectMemberInput;
    if (!d.email || !d.role) throw new ApiError(400, { error: "invalid_body" });
    if (store.members.some((x) => x.projectId === p.id && x.email === d.email))
      throw new ApiError(409, { error: "conflict", message: "member already exists" });
    const now = new Date().toISOString();
    const mem: ProjectMember = {
      userId: `usr_${d.email.split("@")[0].replace(/\./g, "-")}`,
      projectId: p.id, email: d.email,
      name: d.email.split("@")[0].split(".").map((w) => w[0]?.toUpperCase() + w.slice(1)).join(" "),
      role: d.role, addedAt: now,
    };
    store.members.push(mem);
    p.memberCount = store.members.filter((x) => x.projectId === p.id).length;
    return ok({ member: mem }) as unknown as T;
  }
  if (M === "PATCH" && (m = matchPath(path, "/projects/:projectId/members/:userId"))) {
    const p = store.projects.find((x) => x.id === m!.projectId || x.key === m!.projectId);
    if (!p) throw new ApiError(404, { error: "not_found" });
    const mem = store.members.find((x) => x.projectId === p.id && x.userId === m!.userId);
    if (!mem) throw new ApiError(404, { error: "not_found" });
    const d = (body ?? {}) as Partial<Pick<ProjectMember, "role">>;
    if (d.role) mem.role = d.role;
    return ok({ member: mem }) as unknown as T;
  }
  if (M === "DELETE" && (m = matchPath(path, "/projects/:projectId/members/:userId"))) {
    const p = store.projects.find((x) => x.id === m!.projectId || x.key === m!.projectId);
    if (!p) throw new ApiError(404, { error: "not_found" });
    const idx = store.members.findIndex((x) => x.projectId === p.id && x.userId === m!.userId);
    if (idx < 0) throw new ApiError(404, { error: "not_found" });
    store.members.splice(idx, 1);
    p.memberCount = store.members.filter((x) => x.projectId === p.id).length;
    return ok({ ok: true }) as unknown as T;
  }

  // ── infrastructure hierarchy / clusters / search ─────────────────────
  if (M === "GET" && path === "/infrastructure/tree") {
    const preset = (query?.preset as HierarchyPreset | undefined) ?? store.hierarchy.preset;
    if (preset !== store.hierarchy.preset) {
      return ok(buildInfrastructureTree(store.services, store.projects, store.podsByService, store.deployments, preset)) as unknown as T;
    }
    return ok(store.hierarchy) as unknown as T;
  }
  if (M === "GET" && (m = matchPath(path, "/clusters/:id"))) {
    const summary = clusterSummaryFor(m!.id, store.services, store.podsByService);
    return ok(summary) as unknown as T;
  }
  if (M === "GET" && (m = matchPath(path, "/clusters/:id/topology"))) {
    return ok(clusterTopologyFor(m!.id, store.services)) as unknown as T;
  }
  if (M === "GET" && (m = matchPath(path, "/clusters/:id/objects"))) {
    const kind = query?.kind as string | undefined;
    let items = k8sObjectsFor(m!.id, store.services, store.podsByService);
    if (kind) items = items.filter((o) => o.kind === kind);
    return ok({ items, total: items.length }) as unknown as T;
  }
  if (M === "GET" && (m = matchPath(path, "/services/:id/deployment-timeline"))) {
    const svc = store.services.find((s) => s.id === m!.id);
    if (!svc) throw new ApiError(404, { error: "not_found" });
    return ok({ items: deploymentTimelineFor(svc.id, svc.name) }) as unknown as T;
  }
  if (M === "GET" && (m = matchPath(path, "/containers/:serviceId/:podId/:containerName"))) {
    const svc = store.services.find((s) => s.id === m!.serviceId);
    if (!svc) throw new ApiError(404, { error: "not_found" });
    const pods = store.podsByService.get(svc.id) ?? [];
    const pod = pods.find((p) => p.id === m!.podId);
    if (!pod) throw new ApiError(404, { error: "not_found" });
    const detail = containerDetailFor(svc, pod, decodeURIComponent(m!.containerName));
    if (!detail) throw new ApiError(404, { error: "not_found" });
    return ok(detail) as unknown as T;
  }
  if (M === "GET" && path === "/search") {
    const q = String(query?.q ?? "");
    return ok(globalSearch(q, store.services, store.podsByService, store.traces, store.incidents, store.projects)) as unknown as T;
  }

  // ── deployments ──────────────────────────────────────────────────────
  if (M === "GET" && path === "/deployments") {
    const byService = new Map<string, typeof store.deployments[number]>();
    for (const d of store.deployments) {
      const prev = byService.get(d.serviceId);
      if (!prev || new Date(d.createdAt) > new Date(prev.createdAt)) byService.set(d.serviceId, d);
    }
    return ok({ items: [...byService.values()] }) as unknown as T;
  }
  if (M === "GET" && (m = matchPath(path, "/deployments/service/:serviceId"))) {
    return ok({ items: store.deployments.filter((d) => d.serviceId === m!.serviceId) }) as unknown as T;
  }
  if (M === "GET" && (m = matchPath(path, "/deployments/:id"))) {
    const d = store.deployments.find((x) => x.id === m!.id);
    if (!d) throw new ApiError(404, { error: "not_found" });
    return ok(d) as unknown as T;
  }
  if (M === "GET" && (m = matchPath(path, "/deployments/:id/diff"))) return ok(diffFor(m!.id)) as unknown as T;
  if (M === "POST" && (m = matchPath(path, "/deployments/:id/rollback")))
    return ok({ ok: true, message: `Rolled back deployment ${m!.id}` }) as unknown as T;
  if (M === "POST" && matchPath(path, "/deployments/:id/restart"))  return ok({ ok: true }) as unknown as T;
  if (M === "POST" && matchPath(path, "/deployments/:id/redeploy")) return ok({ ok: true }) as unknown as T;

  // ── containers ───────────────────────────────────────────────────────
  if (M === "GET" && (m = matchPath(path, "/containers/service/:serviceId"))) {
    return ok({ pods: store.podsByService.get(m!.serviceId) ?? [] }) as unknown as T;
  }
  if (M === "GET" && (m = matchPath(path, "/containers/service/:serviceId/pod/:podId"))) {
    const pod = (store.podsByService.get(m!.serviceId) ?? []).find((p) => p.id === m!.podId);
    if (!pod) throw new ApiError(404, { error: "not_found" });
    return ok(pod) as unknown as T;
  }

  // ── traces ───────────────────────────────────────────────────────────
  if (M === "GET" && path === "/traces") {
    const q = query ?? {};
    let items = store.traces;
    if (q.service)    items = items.filter((t) => t.services.includes(String(q.service)));
    if (q.q)          items = items.filter((t) => t.rootOperation.toLowerCase().includes(String(q.q).toLowerCase()));
    if (q.onlyErrors) items = items.filter((t) => t.errorCount > 0);
    const limit = Number(q.limit ?? 100);
    return ok({ items: items.slice(0, limit), total: items.length }) as unknown as T;
  }
  if (M === "GET" && (m = matchPath(path, "/traces/:id"))) {
    const trace = store.traces.find((t) => t.id === m!.id);
    if (!trace) throw new ApiError(404, { error: "not_found" });
    return ok({ trace, spans: store.spansByTrace.get(trace.id) ?? [] }) as unknown as T;
  }
  if (M === "POST" && (m = matchPath(path, "/traces/:id/ai-summary"))) {
    const trace = store.traces.find((t) => t.id === m!.id);
    if (!trace) throw new ApiError(404, { error: "not_found" });
    return ok({
      summary:      `Root op '${trace.rootOperation}' spent ~${Math.round(trace.durationMs * 0.6)}ms in a downstream DB call. That single hop dominates end-to-end latency.`,
      criticalPath: (store.spansByTrace.get(trace.id) ?? []).slice(0, 4).map((s) => s.operation),
      suggestedActions: ["Add an index for the top query", "Cache the response for 30s", "Batch calls to the auth service"],
    }) as unknown as T;
  }

  // ── logs ─────────────────────────────────────────────────────────────
  if (M === "GET" && path === "/logs") {
    const q = query ?? {};
    let items = store.logs;
    if (q.service) items = items.filter((l) => l.service === String(q.service));
    if (q.level)   items = items.filter((l) => l.level   === String(q.level));
    if (q.regex) {
      try { const re = new RegExp(String(q.regex), "i"); items = items.filter((l) => re.test(l.message)); }
      catch { throw new ApiError(400, { error: "invalid_regex" }); }
    } else if (q.q) {
      const term = String(q.q).toLowerCase();
      items = items.filter((l) => l.message.toLowerCase().includes(term));
    }
    const limit = Number(q.limit ?? 500);
    return ok({ items: items.slice(-limit).reverse(), total: items.length }) as unknown as T;
  }
  if (M === "GET" && path === "/logs/clusters")    return ok({ items: clusteredLogs() }) as unknown as T;
  if (M === "GET" && path === "/logs/ai-summary") {
    const clusters = clusteredLogs();
    return ok({
      summary: `${clusters.length} distinct error patterns in the last hour. Top: '${clusters[0]?.pattern ?? "n/a"}'.`,
      top: clusters.slice(0, 3).map((c) => c.pattern),
    }) as unknown as T;
  }

  // ── metrics ──────────────────────────────────────────────────────────
  if (M === "GET" && (m = matchPath(path, "/metrics/service/:serviceId"))) {
    const met = store.metrics.get(m!.serviceId);
    if (!met) throw new ApiError(404, { error: "not_found" });
    return ok(met) as unknown as T;
  }

  // ── alerts / rca ─────────────────────────────────────────────────────
  if (M === "GET" && path === "/alerts/rules")     return ok({ items: store.rules })     as unknown as T;
  if (M === "GET" && path === "/alerts/incidents") return ok({ items: store.incidents }) as unknown as T;
  if (M === "POST" && path === "/alerts/preview")  return ok({ willFire: true, wouldMatch: 3, sampleServices: store.services.slice(0, 3).map((s) => s.name) }) as unknown as T;
  if (M === "GET"  && (m = matchPath(path, "/alerts/rca/:serviceId"))) return ok({ causes: store.rcaByService.get(m!.serviceId) ?? [] }) as unknown as T;

  // ── dependencies ─────────────────────────────────────────────────────
  if (M === "GET" && path === "/dependencies") return ok(store.graph) as unknown as T;
  if (M === "GET" && (m = matchPath(path, "/dependencies/service/:serviceId"))) {
    const svc = store.graph.services.find((s) => s.id === m!.serviceId);
    if (!svc) throw new ApiError(404, { error: "not_found" });
    const upstream   = store.graph.edges.filter((e) => e.toServiceId   === svc.id).map((e) => store.graph.services.find((s) => s.id === e.fromServiceId)!).filter(Boolean);
    const downstream = store.graph.edges.filter((e) => e.fromServiceId === svc.id).map((e) => store.graph.services.find((s) => s.id === e.toServiceId)!).filter(Boolean);
    const critical   = store.graph.edges.filter((e) => (e.fromServiceId === svc.id || e.toServiceId === svc.id) && e.errorRatePct > 1);
    return ok({ service: svc, upstream, downstream, critical, blastRadius: downstream.length, circular: [] as string[] }) as unknown as T;
  }

  // ── security ─────────────────────────────────────────────────────────
  if (M === "GET" && path === "/security/summary") {
    const expiring = store.certs.filter((c) => c.status === "expiring").length;
    const expired  = store.certs.filter((c) => c.status === "expired").length;
    return ok({
      mTLS:            { coveragePct: 92 },
      certificates:    { total: store.certs.length, expiring, expired },
      vulns:           {
        critical: store.vulns.filter((v) => v.severity === "critical").length,
        high:     store.vulns.filter((v) => v.severity === "high").length,
        medium:   store.vulns.filter((v) => v.severity === "medium").length,
        low:      store.vulns.filter((v) => v.severity === "low").length,
      },
      networkPolicies: { total: store.netpols.length, enforced: store.netpols.filter((n) => n.enforced).length },
      supplyChain:     { signed: 42, unsigned: 3, provenanceAttested: 38 },
      rbac:            {
        roles: [
          { name: "admin",   permissions: ["*"] },
          { name: "sre",     permissions: ["read", "deploy", "restart"] },
          { name: "viewer",  permissions: ["read"] },
        ],
        bindings: 12,
      },
    }) as unknown as T;
  }
  if (M === "GET" && path === "/security/certificates")      return ok({ items: store.certs })   as unknown as T;
  if (M === "GET" && path === "/security/secrets")           return ok({ items: store.secrets }) as unknown as T;
  if (M === "GET" && path === "/security/network-policies")  return ok({ items: store.netpols }) as unknown as T;
  if (M === "GET" && path === "/security/vulnerabilities")   return ok({ items: store.vulns })   as unknown as T;
  if (M === "GET" && path === "/security/sbom")              return ok({ items: store.sbom })    as unknown as T;

  // ── profiling ────────────────────────────────────────────────────────
  if (M === "GET" && (m = matchPath(path, "/profiling/flamegraph/:serviceId"))) {
    const fg = store.flames.get(m!.serviceId);
    if (!fg) throw new ApiError(404, { error: "not_found" });
    return ok({ flamegraph: fg }) as unknown as T;
  }
  if (M === "GET" && (m = matchPath(path, "/profiling/hot-methods/:serviceId"))) {
    return ok({ items: store.hotMethods.get(m!.serviceId) ?? [] }) as unknown as T;
  }
  if (M === "GET" && path === "/profiling/slow-queries")  return ok({ items: store.slowQueries }) as unknown as T;
  if (M === "GET" && path === "/profiling/heap-analysis") return ok({
    totalMb: 512, breakdown: [
      { label: "Objects",    mb: 260 },
      { label: "Buffers",    mb:  90 },
      { label: "Compiled",   mb:  60 },
      { label: "GC overhead",mb:  40 },
      { label: "Native",     mb:  62 },
    ],
    largestObjects: [
      { class: "OrderLine",       count: 812_400, mb: 88 },
      { class: "Session",         count: 320_000, mb: 42 },
      { class: "CacheEntry",      count: 220_500, mb: 34 },
      { class: "TelemetrySpan",   count: 180_000, mb: 26 },
    ],
  }) as unknown as T;
  if (M === "GET" && path === "/profiling/async-wait") return ok({
    items: [
      { name: "postgres.query",  waitMs: 220, samples: 812 },
      { name: "http.client",     waitMs: 140, samples: 640 },
      { name: "redis.command",   waitMs:  38, samples: 420 },
      { name: "kafka.send",      waitMs:  62, samples: 310 },
      { name: "grpc.call",       waitMs:  84, samples: 240 },
    ],
  }) as unknown as T;

  // ── health ───────────────────────────────────────────────────────────
  if (M === "GET" && (m = matchPath(path, "/health/service/:serviceId"))) {
    const h = store.health.get(m!.serviceId);
    if (!h) throw new ApiError(404, { error: "not_found" });
    return ok(h) as unknown as T;
  }
  if (M === "GET" && path === "/health/summary") {
    return ok({
      items: store.services.slice(0, 20).map((s) => ({ serviceId: s.id, aiScore: store.health.get(s.id)?.aiScore ?? 82 })),
    }) as unknown as T;
  }

  // ── ai / testing / export ────────────────────────────────────────────
  if (M === "POST" && path === "/ai/ask") {
    const b = (body ?? {}) as { text?: string; scopeServiceId?: string };
    const target = (b.scopeServiceId ? store.services.find((s) => s.id === b.scopeServiceId) : undefined)
                ?? store.services.find((s) => b.text && s.name.toLowerCase().includes((b.text ?? "").toLowerCase()))
                ?? store.services.find((s) => s.status === "critical")
                ?? store.services[0];
    if (!target) throw new ApiError(404, { error: "no_services_available" });
    return ok({
      summary: `${target.name} is showing elevated p95 latency (${target.latencyP95Ms}ms). The most likely cause is a recent code change in the checkout path — evidence linked below.`,
      metrics: [`latency.p99 · ${target.name}`, `err.rate · ${target.name}`, `db.query.duration · postgres-primary`],
      traces:  store.traces.filter((t) => t.services.includes(target.name)).slice(0, 3).map((t) => t.id),
      logs:    store.logs.filter((l) => l.service === target.name && l.level === "error").slice(0, 3).map((l) => l.id),
      dependencies: store.graph.edges
        .filter((e) => e.fromServiceId === target.id || e.toServiceId === target.id)
        .slice(0, 4)
        .map((e) => `${e.fromServiceId} → ${e.toServiceId}`),
      deployments: store.deployments.filter((d) => d.serviceId === target.id).slice(0, 2).map((d) => d.id),
      suggestedFixes: [
        "Revert to previous revision and confirm p99 recovers",
        "Add a composite index on order_lines(order_id, sku)",
        "Raise HTTP timeout to inventory-service from 200ms to 500ms",
      ],
      potentialRootCause: "Regression from the latest deploy — a slow SQL join was introduced in checkout.",
      possibleRollback: (() => {
        const d = store.deployments.find((x) => x.serviceId === target.id);
        return d ? { deploymentId: d.id, label: "Rollback to previous revision" } : undefined;
      })(),
      investigationId: `inv_${Date.now().toString(36)}`,
    }) as unknown as T;
  }
  if (M === "POST" && path === "/testing/run") {
    const b = (body ?? {}) as { serviceId: string; kind: string };
    return ok({
      testId: `t_${Date.now().toString(36)}`,
      kind:   b.kind,
      passed: true,
      durationMs: 1_200,
      checks: [
        { id: "prepare", label: "Prepare",  status: "passed", durationMs: 120 },
        { id: "connect", label: "Connect",  status: "passed", durationMs: 220 },
        { id: "sample",  label: "Sample",   status: "passed", durationMs: 460 },
        { id: "verify",  label: "Verify",   status: "passed", durationMs: 400 },
      ],
      metrics: { p95: 148, p99: 245, throughputRps: 1_240, errorRate: 0.4 },
    }) as unknown as T;
  }

  throw new ApiError(404, { error: "no_mock_handler", method: M, path });
}
